<script setup lang="ts">
import client from "@/utils/axios";
import { errorToast, successToast } from "@/utils/custom-toast";
import { defineEmits, defineProps, ref, computed } from "vue";
import QuestionnaireModal from "@/components/Questionnaire/parts/QuestionnaireModal.vue";
import QuestionnaireProgressBar from "@/components/Questionnaire/parts/QuestionnaireProgressBar.vue";
import AbnahmeInitial from "@/components/Questionnaire/abnahme/AbnahmeInitial.vue";
import AbnahmeApointmentCompleted from "@/components/Questionnaire/abnahme/AbnahmeApointmentCompleted.vue";
import AbnahmeAppointmentNotCompleted from "@/components/Questionnaire/abnahme/AbnahmeAppointmentNotCompleted.vue";
import AbnahmeAppointmentFinal from "@/components/Questionnaire/abnahme/AbnahmeAppointmentFinal.vue";
import { Question } from "@/types";
import AbnahmeDataMissingTextArea from "../Questionnaire/abnahme/AbnahmeDataMissingTextArea.vue";
import { parseQcAbnahmeAnswers } from "@/utils/parse-qc-abnahme-answers";
import { useTranslate } from "@/composables/useTranslate";

type Props = {
  isAbnahmeModalOpen: boolean;
  projectId: string;
  phaseId: number;
  phaseDetails: any;
};

const props = defineProps<Props>();

type Emits = {
  (event: "onClose"): void;
  (event: "onSubmit"): void;
  (event: "generateReport"): void;
};
const emits = defineEmits<Emits>();
const { t } = useTranslate();
const questions = [
  {
    slug: "abnahme-initial",
    modalTitle: t("questionnaire.completing_abnahme").value,
    question: t("questionnaire.was_appointment_completed").value,
    answers: ["abnahme-outcome", "appointment-not-completed"],
    value: "",
  },
  {
    slug: "abnahme-outcome",
    modalTitle: t("questionnaire.completing_abnahme").value,
    question: t("questionnaire.what_is_outcome_abnahme").value,
    answers: ["abnahme-passed", "data-missing", "abnahme-failed"],
    value: "",
  },
  {
    slug: "abnahme-passed",
    modalTitle: t("questionnaire.review_abnahme").value,
    answers: [],
    data: {
      ibnData: "",
    },
    value: "",
  },
  {
    slug: "data-missing",
    modalTitle: t("questionnaire.completing_abnahme").value,
    answers: [],
    data: {
      description: "",
    },
    value: "",
  },
  {
    slug: "abnahme-failed",
    modalTitle: t("questionnaire.completing_abnahme").value,
    answers: [],
    data: {
      description: "",
      whatToSchedule: "",
      days: 0,
      missingMaterial: "",
      dispoNotes: "",
      fieldInstruction: "",
    },
    value: "",
  },
  {
    slug: "data-missing-review",
    modalTitle: t("questionnaire.review_abnahme").value,
    answers: [],
    data: null,
    value: "",
  },
  {
    slug: "appointment-not-completed",
    modalTitle: t("questionnaire.completing_abnahme").value,
    question: t("questionnaire.completing_abnahme").value,
    answers: [],
    data: {
      description: "",
      whatToSchedule: "",
      days: 0,
      missingMaterial: "",
      dispoNotes: "",
      fieldInstruction: "",
    },
  },
  {
    slug: "abnahme-failed-review",
    modalTitle: t("questionnaire.review_abnahme").value,
    answers: [],
    data: null,
    value: "",
  },
  {
    slug: "appointment-not-completed-review",
    modalTitle: t("questionnaire.review_abnahme").value,
    answers: [],
    data: null,
    value: "",
  },
];

const questionnaireProgress = computed(() => {
  let percentage = 100;
  if (selectedQuestion.value && selectedQuestion.value) {
    switch (selectedQuestion.value.slug) {
      case "abnahme-initial":
        percentage = 15;
        break;

      case "abnahme-outcome":
        percentage = 50;
        break;
      case "appointment-not-completed":
      case "data-missing":
      case "abnahme-failed":
        percentage = 70;
        break;
      case "appointment-not-completed-review":
      case "data-missing-review":
      case "abnahme-failed-review":
        percentage = 100;
        break;
    }
  }

  return percentage;
});

const questionsState = ref<Array<Question>>([...questions]);
const isModalOpen = computed(() => props.isAbnahmeModalOpen);
const isLoading = ref(false);
const selectedQuestion = ref<Question | undefined>(
  questionsState.value
    ? questionsState.value.find(
        (item: Question) => item.slug === "abnahme-initial"
      )
    : undefined
);

const dataMissingReviewQuestions = computed(() => {
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    return [
      {
        question: "What data is missing? Please write here",
        answer: dataMissingQuestion.data.description,
      },
    ];
  }
  return [];
});

const abnahmeFailedReviewQuestions = computed(() => {
  let abnahmeFailedData = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-failed"
  );
  if (abnahmeFailedData && abnahmeFailedData.data) {
    let tempQuestions = parseQcAbnahmeAnswers(
      abnahmeFailedData.data,
      "abnahme"
    );
    return tempQuestions;
  }
  return [];
});

const appointmentNotCompletedReviewQuestions = computed(() => {
  let appointmentNotCompleted = questionsState.value.find(
    (item: Question) => item.slug === "appointment-not-completed"
  );
  if (appointmentNotCompleted && appointmentNotCompleted.data) {
    let tempQuestions = parseQcAbnahmeAnswers(
      appointmentNotCompleted.data,
      "appointment"
    );
    return tempQuestions;
  }
  return [];
});

const currentPhase = computed(() => {
  let currentPhaseProgress = props.phaseDetails?.phases.find(
    (phase: any) => phase.phaseId === props.phaseId
  );
  return currentPhaseProgress;
});

const onClose = () => {
  if (questionsState.value) {
    questionsState.value = [...questions];
  }
  selectedQuestion.value = questionsState.value
    ? questionsState.value.find(
        (item: Question) => item.slug === "abnahme-initial"
      )
    : undefined;
  emits("onClose");
};

const abnahmeInitialOnSubmit = (message: string) => {
  let tempQuestions = questionsState.value.map((item: Question) => {
    if (item.slug === "abnahme-initial") {
      item.value = message;
    }
    return item;
  });

  questionsState.value = tempQuestions;
  let nextQuestion = "";
  if (message && message.includes("Yes")) {
    nextQuestion = "abnahme-outcome";
  } else {
    nextQuestion = "appointment-not-completed";
  }

  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === nextQuestion
  );
};

const onAbnahmePassed = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-passed"
  );
};

const onAbnahmePassedSubmit = (ibnDate: string, generateReport?: boolean) => {
  let payload = {
    actionId: 4,
    ibnDate: ibnDate,
  };

  onSubmit(payload, generateReport);
};

const onDataMissing = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
};

const dataMissingOnSubmit = (description: string) => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "data-missing-review"
  );
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    dataMissingQuestion.data.description = description;
  }
};

const dataMissingReviewOnSubmit = () => {
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    let payload = {
      actionId: 3,
      description: dataMissingQuestion.data.description,
    };
    onSubmit(payload);
  }
};

const onAbnahmeFailed = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-failed"
  );
};

const onAbnahmeFailedSubmit = (payload: {
  description: string;
  whatToSchedule: string;
  days: string;
  missingMaterial: string;
  dispoNotes: string;
  fieldInstruction: string;
}) => {
  let abnahmeFailedQuestion = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-failed"
  );
  if (abnahmeFailedQuestion && abnahmeFailedQuestion.data) {
    abnahmeFailedQuestion.data = payload;
  }
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-failed-review"
  );
};

const onAbnahmeFailedReviewSubmit = () => {
  let abnahmeFailedQuestion = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-failed"
  );
  if (abnahmeFailedQuestion && abnahmeFailedQuestion.data) {
    let payload = {
      actionId: 2,
      metadata: abnahmeFailedQuestion.data,
    };
    onSubmit(payload);
  }
};

const onAppointmentNotCompletedSubmit = (payload: {
  description: string;
  whatToSchedule: string;
  days: string;
  missingMaterial: string;
  dispoNotes: string;
  fieldInstruction: string;
}) => {
  let appointmentNotCompletedQuestion = questionsState.value.find(
    (item: Question) => item.slug === "appointment-not-completed"
  );
  if (appointmentNotCompletedQuestion && appointmentNotCompletedQuestion.data) {
    appointmentNotCompletedQuestion.data = payload;
  }
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "appointment-not-completed-review"
  );
};

const onAppointmentNotCompletedReviewSubmit = () => {
  let appointmentNotCompletedQuestions = questionsState.value.find(
    (item: Question) => item.slug === "appointment-not-completed"
  );
  if (
    appointmentNotCompletedQuestions &&
    appointmentNotCompletedQuestions.data
  ) {
    let payload = {
      actionId: 1,
      metadata: appointmentNotCompletedQuestions.data,
    };
    onSubmit(payload);
  }
};

const onStepBack = (goBackToQuestionSlug: string) => {
  let backQuestion = questionsState.value.find(
    (item: Question) => item.slug === goBackToQuestionSlug
  );

  if (backQuestion) {
    selectedQuestion.value = backQuestion;
  }
};

const onSubmit = async (payload: any, generateReport?: boolean) => {
  try {
    isLoading.value = true;
    payload.isQc = false;
    let resp = await client.post(
      `/projects/${props.projectId}/phase/${props.phaseId}/complete-abnahme`,
      payload
    );

    if (resp.status === 200) {
      successToast("QC action saved.");
      if (generateReport) {
        emits("generateReport");
      }
    }
    isLoading.value = false;
    onClose();
    emits("onClose");
  } catch (error: any) {
    console.error(error);
    errorToast(error);
    isLoading.value = false;
  }
};
</script>

<template>
  <Transition name="nested">
    <div v-if="isModalOpen">
      <div
        class="w-full h-screen fixed top-0 left-0 flex items-start justify-center bg-black/75 z-[1000]"
      >
        <QuestionnaireModal class="modal" @on-close="onClose">
          <template #header>
            <div class="flex gap-2 items-center">
              <img
                class="w-4 h-4 cursor-pointer"
                src="@/assets/pencil-gray.svg"
                alt="Pencil"
              />
              <h2 class="text-xl font-semibold">
                {{ selectedQuestion ? selectedQuestion.modalTitle : "" }}
              </h2>
            </div>
          </template>
          <template #progress-bar>
            <QuestionnaireProgressBar
              :progress="questionnaireProgress"
              :warning="false"
            />
          </template>
          <template #content>
            <AbnahmeInitial
              v-if="
                selectedQuestion && selectedQuestion?.slug === 'abnahme-initial'
              "
              @on-submit="abnahmeInitialOnSubmit"
              :appointment-completed-button-text="
                t('questionnaire.yes_it_is').value
              "
              :appointment-not-completed-button-text="
                t('questionnaire.no_there_was_issue').value
              "
            />
            <AbnahmeApointmentCompleted
              v-if="
                selectedQuestion && selectedQuestion?.slug === 'abnahme-outcome'
              "
              :appointment-completed-button-text="
                t('questionnaire.abnahme_passed').value
              "
              :data-is-missing-button-text="
                t('questionnaire.data_is_missing').value
              "
              :apointment-failed-button-text="
                t('questionnaire.abnahme_failed').value
              "
              :prev-button-text="t('questionnaire.step_back').value"
              @on-abnahme-passed="onAbnahmePassed"
              @on-data-missing="onDataMissing"
              @on-abnahme-failed="onAbnahmeFailed"
              @on-step-back="onStepBack('abnahme-initial')"
            />
            <AbnahmeDataMissingTextArea
              v-if="
                selectedQuestion && selectedQuestion?.slug === 'data-missing'
              "
              :textAreaTitle="t('questionnaire.what_data_is_missing').value"
              :textAreaPlaceholder="t('questionnaire.description').value"
              :prevButtonText="t('questionnaire.step_back').value"
              :nextButtonText="t('questionnaire.next').value"
              height="h-[203px]"
              :description="questionsState.find( (item: Question) =>
                  item.slug === 'data-missing')?.data.description"
              @on-submit="dataMissingOnSubmit"
              @on-step-back="onStepBack('abnahme-outcome')"
            />
            <AbnahmeAppointmentNotCompleted
              v-if="
                selectedQuestion && selectedQuestion?.slug === 'abnahme-failed'
              "
              :title="t('questionnaire.abnahme_failed').value"
              :next-button-text="t('questionnaire.next').value"
              :prev-button-text="t('questionnaire.step_back').value"
              :text-area-placeholder-one="t('questionnaire.description').value"
              :text-area-title-one="
                t('questionnaire.write_what_happened').value
              "
              :select-menu-data-one="[
                { id: 'ac', name: 'AC' },
                { id: 'dc', name: 'DC' },
                { id: 'both', name: 'Both' },
              ]"
              :select-menu-data-one-title="
                t('questionnaire.what_do_we_schedule').value
              "
              :text-field-one-text="t('questionnaire.how_many_days').value"
              :text-field-one-placeholder="t('questionnaire.number').value"
              :text-area-title-two="
                t('questionnaire.what_material_is_missing').value
              "
              :text-area-placeholder-two="t('questionnaire.description').value"
              :text-area-title-three="t('questionnaire.dispo_notes').value"
              :text-area-placeholder-three="
                t('questionnaire.description').value
              "
              :text-area-title-four="
                t('questionnaire.what_needs_to_be_done').value
              "
              :text-area-placeholder-four="t('questionnaire.description').value"
              :text-field-one-required="true"
              :data="questionsState.find(
                (item: Question) => item.slug === 'abnahme-failed' )?.data"
              @on-step-back="onStepBack('abnahme-outcome')"
              @on-submit="onAbnahmeFailedSubmit"
            />
            <AbnahmeAppointmentNotCompleted
              v-if="
                selectedQuestion &&
                selectedQuestion?.slug === 'appointment-not-completed'
              "
              :data="questionsState.find(
                (item: Question) => item.slug === 'appointment-not-completed' )?.data"
              title="Appointment not completed"
              :next-button-text="t('questionnaire.next').value"
              :prev-button-text="t('questionnaire.step_back').value"
              :text-area-placeholder-one="t('questionnaire.description').value"
              :text-area-title-one="
                t('questionnaire.write_what_happened').value
              "
              :select-menu-data-one="[
                {
                  id: 'ac',
                  name: 'AC',
                },
                {
                  id: 'dc',
                  name: 'DC',
                },
                {
                  id: 'both',
                  name: 'Both',
                },
              ]"
              :select-menu-data-one-title="
                t('questionnaire.what_do_we_schedule').value
              "
              :text-field-one-text="t('questionnaire.how_many_days').value"
              :text-field-one-placeholder="t('questionnaire.number').value"
              :text-area-title-two="
                t('questionnaire.what_material_is_missing').value
              "
              :text-area-placeholder-two="t('questionnaire.description').value"
              :text-area-title-three="t('questionnaire.dispo_notes').value"
              :text-area-placeholder-three="
                t('questionnaire.description').value
              "
              :text-area-title-four="
                t('questionnaire.what_needs_to_be_done').value
              "
              :text-area-placeholder-four="t('questionnaire.description').value"
              :text-field-one-required="true"
              @on-step-back="onStepBack('abnahme-initial')"
              @on-submit="onAppointmentNotCompletedSubmit"
            />
            <AbnahmeAppointmentFinal
              v-if="
                selectedQuestion &&
                selectedQuestion?.slug === 'data-missing-review'
              "
              :current-phase="currentPhase"
              :phase-details="phaseDetails"
              :is-loading="isLoading"
              status="data-missing"
              :done-button-text="t('questionnaire.submit').value"
              :final-title="t('questionnaire.data_missing').value"
              :prev-button-text="t('questionnaire.back_to_edit').value"
              :answers="dataMissingReviewQuestions"
              @on-step-back="onStepBack('data-missing')"
              @on-submit="dataMissingReviewOnSubmit"
            />
            <AbnahmeAppointmentFinal
              v-if="
                selectedQuestion && selectedQuestion?.slug === 'abnahme-passed'
              "
              :current-phase="currentPhase"
              :phase-details="phaseDetails"
              :is-loading="isLoading"
              :text-field-title="`IBN date ${
                currentPhase?.phaseId === 2
                  ? '(' + t('global.required').value + ')'
                  : '(' + t('global.optional').value + ')'
              }`"
              status="success"
              :done-button-text="t('questionnaire.done').value"
              :final-title="t('questionnaire.fantastic').value"
              :final-subtitle="t('questionnaire.abnahme_passed').value"
              :prev-button-text="t('questionnaire.back_to_edit').value"
              @on-step-back="onStepBack('abnahme-outcome')"
              @on-submit="onAbnahmePassedSubmit"
            />
            <AbnahmeAppointmentFinal
              v-if="
                selectedQuestion &&
                selectedQuestion?.slug === 'abnahme-failed-review'
              "
              :current-phase="currentPhase"
              :phase-details="phaseDetails"
              :is-loading="isLoading"
              status="failed"
              :done-button-text="t('questionnaire.submit').value"
              :final-title="t('questionnaire.abnahme_failed').value"
              :prev-button-text="t('questionnaire.back_to_edit').value"
              :answers="abnahmeFailedReviewQuestions"
              @on-step-back="onStepBack('abnahme-failed')"
              @on-submit="onAbnahmeFailedReviewSubmit"
            />
            <AbnahmeAppointmentFinal
              v-if="
                selectedQuestion &&
                selectedQuestion?.slug === 'appointment-not-completed-review'
              "
              :current-phase="currentPhase"
              :phase-details="phaseDetails"
              status="failed"
              :done-button-text="t('questionnaire.submit').value"
              :final-title="t('questionnaire.appointment_not_completed').value"
              :prev-button-text="t('questionnaire.back_to_edit').value"
              :is-loading="isLoading"
              :answers="appointmentNotCompletedReviewQuestions"
              @on-step-back="onStepBack('appointment-not-completed')"
              @on-submit="onAppointmentNotCompletedReviewSubmit"
            />
          </template>
        </QuestionnaireModal>
      </div>
    </div>
  </Transition>
</template>
